/**
 * User preferences hook for managing display settings, canvas preferences, and consents
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../lib/api";
import type { UserPreferences } from "../types/preferences";

/**
 * Hook for managing user preferences
 */
export function useUserPreferences() {
    const { user, loading: authLoading } = useAuth();
    const queryClient = useQueryClient();

    // Fetch preferences - only when authenticated
    const {
        data: preferencesData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["user-preferences"],
        queryFn: () => apiClient.getUserPreferences(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        enabled: !!user && !authLoading, // Only fetch when user is authenticated
    });

    // Update preferences mutation
    const updatePreferencesMutation = useMutation({
        mutationFn: (preferences: UserPreferences) => apiClient.updateUserPreferences(preferences),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
        },
    });

    return {
        preferences: preferencesData?.preferences,
        isLoading,
        error,
        updatePreferences: updatePreferencesMutation.mutate,
        isUpdating: updatePreferencesMutation.isPending,
        updateError: updatePreferencesMutation.error,
    };
}

/**
 * Hook for managing user consents
 */
export function useUserConsents() {
    const { user, loading: authLoading } = useAuth();
    const queryClient = useQueryClient();

    // Fetch consents - only when authenticated
    const {
        data: consentsData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["user-consents"],
        queryFn: () => apiClient.getUserConsents(),
        staleTime: 30 * 60 * 1000, // 30 minutes
        retry: 1,
        enabled: !!user && !authLoading, // Only fetch when user is authenticated
    });

    // Update consent mutation
    const updateConsentMutation = useMutation({
        mutationFn: ({ consentType, consented }: { consentType: string; consented: boolean }) =>
            apiClient.updateUserConsent(consentType, consented),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-consents"] });
        },
    });

    return {
        consents: consentsData?.consents,
        isLoading,
        error,
        updateConsent: updateConsentMutation.mutate,
        isUpdating: updateConsentMutation.isPending,
        updateError: updateConsentMutation.error,
    };
}

/**
 * Simplified hook for just checking if voice button should be shown
 */
export function useShowVoiceButton(): boolean {
    const { user } = useAuth();
    const { preferences } = useUserPreferences();

    // If not authenticated, don't show voice button
    if (!user) return false;

    return preferences?.display?.showVoiceButton ?? true; // default to true for authenticated users
}
